import React, { useState, useEffect } from 'react';
import { FaSave, FaCheck, FaPlus, FaTimes, FaGripVertical } from 'react-icons/fa';
import { useSiteContent } from '../../hooks/useSiteContent';
import { useHomeContent } from '../../hooks/useHomeContent';

const TABS = [
  { key: 'home', label: 'Accueil' },
  { key: 'team', label: 'Équipe' },
  { key: 'partners', label: 'Partenaires' },
  { key: 'contact', label: 'Contact' },
  { key: 'history', label: 'Histoire' },
  { key: 'rules', label: 'Règles de vie' }
];

function SiteContentEditor() {
  const { content, loading, updateContent } = useSiteContent();
  const { content: homeContent, loading: homeLoading, updateContent: updateHomeContent } = useHomeContent();
  const [activeTab, setActiveTab] = useState('home');
  const [form, setForm] = useState(null);
  const [homeForm, setHomeForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (content) setForm(JSON.parse(JSON.stringify(content)));
  }, [content]);

  useEffect(() => {
    if (homeContent) setHomeForm(JSON.parse(JSON.stringify(homeContent)));
  }, [homeContent]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      if (activeTab === 'home') {
        await updateHomeContent(homeForm);
      } else {
        await updateContent(form);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const isLoading = activeTab === 'home' ? (homeLoading || !homeForm) : (loading || !form);
  if (isLoading) return <p style={{ color: '#888', textAlign: 'center', padding: '40px' }}>Chargement...</p>;

  return (
    <div>
      <div className="admin-page-header">
        <h1>Contenu du site</h1>
        <button onClick={handleSave} disabled={saving} className="admin-btn admin-btn-primary" style={{ opacity: saving ? 0.7 : 1 }}>
          {saved ? <><FaCheck /> Sauvegardé</> : saving ? <><FaSave /> Sauvegarde...</> : <><FaSave /> Sauvegarder</>}
        </button>
      </div>

      <div className="admin-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`admin-tab${activeTab === tab.key ? ' active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="admin-card">
        {activeTab === 'home' && homeForm && <HomeEditor form={homeForm} setForm={setHomeForm} />}
        {activeTab === 'team' && form && <TeamEditor form={form} setForm={setForm} />}
        {activeTab === 'partners' && form && <PartnersEditor form={form} setForm={setForm} />}
        {activeTab === 'contact' && form && <ContactEditor form={form} setForm={setForm} />}
        {activeTab === 'history' && form && form.history && <HistoryEditor form={form} setForm={setForm} />}
        {activeTab === 'rules' && form && form.rules && <RulesEditor form={form} setForm={setForm} />}
      </div>
    </div>
  );
}

function HomeEditor({ form, setForm }) {
  const updateHome = (field, value) => setForm({ ...form, home: { ...form.home, [field]: value } });
  const updateActions = (field, value) => setForm({ ...form, actions: { ...form.actions, [field]: value } });
  const h = form.home;
  const a = form.actions;

  return (
    <div>
      <SectionTitle>Hero</SectionTitle>
      <FormGroup label="Titre principal">
        <input type="text" value={h.heroTitle} onChange={e => updateHome('heroTitle', e.target.value)} className="admin-input" />
      </FormGroup>
      <FormGroup label="Description">
        <textarea value={h.heroDescription} onChange={e => updateHome('heroDescription', e.target.value)} rows={3} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>

      <SectionTitle>Citation / Mission</SectionTitle>
      <FormGroup label="Citation mise en avant" hint="Texte qui apparaît dans le bloc citation de la page d'accueil.">
        <textarea value={h.missionQuote} onChange={e => updateHome('missionQuote', e.target.value)} rows={4} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>

      <SectionTitle>Informations pratiques</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <FormGroup label="Adresse" hint="Utilisez <br /> pour un saut de ligne.">
          <textarea value={h.addressValue} onChange={e => updateHome('addressValue', e.target.value)} rows={2} className="admin-input" style={{ resize: 'vertical' }} />
        </FormGroup>
        <FormGroup label="Horaires" hint="Utilisez <br /> pour un saut de ligne.">
          <textarea value={h.hoursValue} onChange={e => updateHome('hoursValue', e.target.value)} rows={2} className="admin-input" style={{ resize: 'vertical' }} />
        </FormGroup>
      </div>

      <SectionTitle>Cartes de services</SectionTitle>
      {[1, 2, 3, 4, 5, 6].map(n => (
        <ItemCard key={n} onDelete={null}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
            <FormGroup label={`Service ${n} — Titre`}>
              <input type="text" value={h[`service${n}Title`]} onChange={e => updateHome(`service${n}Title`, e.target.value)} className="admin-input" />
            </FormGroup>
            <FormGroup label="Description">
              <input type="text" value={h[`service${n}Desc`]} onChange={e => updateHome(`service${n}Desc`, e.target.value)} className="admin-input" />
            </FormGroup>
          </div>
        </ItemCard>
      ))}

      <SectionTitle>Section don</SectionTitle>
      <FormGroup label="Titre">
        <input type="text" value={h.donationTitle} onChange={e => updateHome('donationTitle', e.target.value)} className="admin-input" />
      </FormGroup>
      <FormGroup label="Description">
        <textarea value={h.donationDesc} onChange={e => updateHome('donationDesc', e.target.value)} rows={2} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>
      <FormGroup label="Note fiscale">
        <input type="text" value={h.donationNote} onChange={e => updateHome('donationNote', e.target.value)} className="admin-input" />
      </FormGroup>

      <SectionTitle>Restauration (Nos actions)</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <FormGroup label="Petit déjeuner — Horaire">
          <input type="text" value={a.mealBreakfastTime} onChange={e => updateActions('mealBreakfastTime', e.target.value)} className="admin-input" />
        </FormGroup>
        <FormGroup label="Repas de midi — Horaire">
          <input type="text" value={a.mealLunchTime} onChange={e => updateActions('mealLunchTime', e.target.value)} className="admin-input" />
        </FormGroup>
      </div>
      <FormGroup label="Description du repas" hint="Peut contenir du HTML (<strong>, <em>…).">
        <textarea value={a.mealDesc} onChange={e => updateActions('mealDesc', e.target.value)} rows={3} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>

      <SectionTitle>Douches</SectionTitle>
      <FormGroup label="Horaires douches">
        <input type="text" value={a.showerTime} onChange={e => updateActions('showerTime', e.target.value)} className="admin-input" />
      </FormGroup>
      <FormGroup label="Description douches">
        <textarea value={a.showerDesc} onChange={e => updateActions('showerDesc', e.target.value)} rows={2} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>

      <SectionTitle>Laverie</SectionTitle>
      <FormGroup label="Description laverie">
        <textarea value={a.laundryDesc} onChange={e => updateActions('laundryDesc', e.target.value)} rows={2} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>

      <SectionTitle>Bagagerie</SectionTitle>
      <FormGroup label="Description bagagerie">
        <textarea value={a.baggageDesc} onChange={e => updateActions('baggageDesc', e.target.value)} rows={2} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>
      <FormGroup label="Note bagagerie">
        <input type="text" value={a.baggageNote} onChange={e => updateActions('baggageNote', e.target.value)} className="admin-input" />
      </FormGroup>
    </div>
  );
}

function FormGroup({ label, hint, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label className="admin-label">{label}</label>
      {hint && <p style={{ fontSize: '12px', color: '#aaa', margin: '0 0 6px' }}>{hint}</p>}
      {children}
    </div>
  );
}

function TeamEditor({ form, setForm }) {
  const update = (field, value) => setForm({ ...form, team: { ...form.team, [field]: value } });

  return (
    <div>
      <FormGroup label="Titre de la page">
        <input type="text" value={form.team.title} onChange={e => update('title', e.target.value)} className="admin-input" />
      </FormGroup>
      <FormGroup label="Introduction">
        <textarea value={form.team.intro} onChange={e => update('intro', e.target.value)} rows={4} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>
      <FormGroup label="Image de l'équipe">
        <input type="text" value={form.team.image} onChange={e => update('image', e.target.value)} className="admin-input" placeholder="/uploads/..." />
        {form.team.image && <img src={form.team.image} alt="Preview" style={{ marginTop: '8px', maxHeight: '100px', borderRadius: '6px' }} />}
      </FormGroup>

      <SectionTitle>Membres de l'équipe</SectionTitle>
      {form.team.members.map((member, i) => (
        <ItemCard key={i} onDelete={() => {
          const members = form.team.members.filter((_, j) => j !== i);
          update('members', members);
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <FormGroup label="Nom">
              <input type="text" value={member.name} onChange={e => {
                const members = [...form.team.members];
                members[i] = { ...members[i], name: e.target.value };
                update('members', members);
              }} className="admin-input" />
            </FormGroup>
            <FormGroup label="Rôle">
              <input type="text" value={member.role} onChange={e => {
                const members = [...form.team.members];
                members[i] = { ...members[i], role: e.target.value };
                update('members', members);
              }} className="admin-input" />
            </FormGroup>
          </div>
          <FormGroup label="Description">
            <textarea value={member.description} onChange={e => {
              const members = [...form.team.members];
              members[i] = { ...members[i], description: e.target.value };
              update('members', members);
            }} rows={2} className="admin-input" style={{ resize: 'vertical' }} />
          </FormGroup>
        </ItemCard>
      ))}
      <AddButton onClick={() => update('members', [...form.team.members, { name: '', role: '', description: '' }])}>
        Ajouter un membre
      </AddButton>

      <SectionTitle>Psychologue</SectionTitle>
      {form.team.psychologist && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <FormGroup label="Nom">
            <input type="text" value={form.team.psychologist.name} onChange={e => setForm({ ...form, team: { ...form.team, psychologist: { ...form.team.psychologist, name: e.target.value } } })} className="admin-input" />
          </FormGroup>
          <FormGroup label="Rôle">
            <input type="text" value={form.team.psychologist.role} onChange={e => setForm({ ...form, team: { ...form.team, psychologist: { ...form.team.psychologist, role: e.target.value } } })} className="admin-input" />
          </FormGroup>
          <FormGroup label="Organisation">
            <input type="text" value={form.team.psychologist.organization} onChange={e => setForm({ ...form, team: { ...form.team, psychologist: { ...form.team.psychologist, organization: e.target.value } } })} className="admin-input" />
          </FormGroup>
          <FormGroup label="URL Organisation">
            <input type="url" value={form.team.psychologist.organizationUrl} onChange={e => setForm({ ...form, team: { ...form.team, psychologist: { ...form.team.psychologist, organizationUrl: e.target.value } } })} className="admin-input" />
          </FormGroup>
          <FormGroup label="Description">
            <input type="text" value={form.team.psychologist.description} onChange={e => setForm({ ...form, team: { ...form.team, psychologist: { ...form.team.psychologist, description: e.target.value } } })} className="admin-input" />
          </FormGroup>
        </div>
      )}

      <SectionTitle>Activités bénévoles</SectionTitle>
      <FormGroup label="Matin">
        <textarea value={form.team.volunteerActivities.morning} onChange={e => setForm({ ...form, team: { ...form.team, volunteerActivities: { ...form.team.volunteerActivities, morning: e.target.value } } })} rows={3} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>
      <FormGroup label="Après-midi">
        <textarea value={form.team.volunteerActivities.afternoon} onChange={e => setForm({ ...form, team: { ...form.team, volunteerActivities: { ...form.team.volunteerActivities, afternoon: e.target.value } } })} rows={3} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>
      <FormGroup label="Sorties">
        <textarea value={form.team.volunteerActivities.outings} onChange={e => setForm({ ...form, team: { ...form.team, volunteerActivities: { ...form.team.volunteerActivities, outings: e.target.value } } })} rows={3} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>
    </div>
  );
}

function PartnersEditor({ form, setForm }) {
  const update = (field, value) => setForm({ ...form, partners: { ...form.partners, [field]: value } });

  return (
    <div>
      <FormGroup label="Titre de la page">
        <input type="text" value={form.partners.title} onChange={e => update('title', e.target.value)} className="admin-input" />
      </FormGroup>
      <FormGroup label="Introduction">
        <textarea value={form.partners.intro} onChange={e => update('intro', e.target.value)} rows={3} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>
      <FormGroup label="Texte sur le financement">
        <textarea value={form.partners.funding} onChange={e => update('funding', e.target.value)} rows={2} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>

      <SectionTitle>Liste des partenaires</SectionTitle>
      {form.partners.list.map((partner, i) => (
        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
          <FaGripVertical style={{ color: '#ccc', flexShrink: 0 }} />
          <input type="text" value={partner.name} onChange={e => {
            const list = [...form.partners.list];
            list[i] = { ...list[i], name: e.target.value };
            update('list', list);
          }} className="admin-input" style={{ flex: 2 }} placeholder="Nom du partenaire" />
          <input type="text" value={partner.url || ''} onChange={e => {
            const list = [...form.partners.list];
            list[i] = { ...list[i], url: e.target.value || null };
            update('list', list);
          }} className="admin-input" style={{ flex: 1 }} placeholder="URL (optionnel)" />
          <button onClick={() => update('list', form.partners.list.filter((_, j) => j !== i))} className="admin-btn admin-btn-danger" style={{ flexShrink: 0 }}>
            <FaTimes />
          </button>
        </div>
      ))}
      <AddButton onClick={() => update('list', [...form.partners.list, { name: '', url: null }])}>
        Ajouter un partenaire
      </AddButton>

      <SectionTitle>Logos</SectionTitle>
      {form.partners.logos && form.partners.logos.map((logo, i) => (
        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
          <input type="text" value={logo.src} onChange={e => {
            const logos = [...form.partners.logos];
            logos[i] = { ...logos[i], src: e.target.value };
            update('logos', logos);
          }} className="admin-input" style={{ flex: 2 }} placeholder="URL de l'image" />
          <input type="text" value={logo.alt} onChange={e => {
            const logos = [...form.partners.logos];
            logos[i] = { ...logos[i], alt: e.target.value };
            update('logos', logos);
          }} className="admin-input" style={{ flex: 1 }} placeholder="Texte alternatif" />
          <button onClick={() => update('logos', form.partners.logos.filter((_, j) => j !== i))} className="admin-btn admin-btn-danger" style={{ flexShrink: 0 }}>
            <FaTimes />
          </button>
        </div>
      ))}
      <AddButton onClick={() => update('logos', [...(form.partners.logos || []), { src: '', alt: '' }])}>
        Ajouter un logo
      </AddButton>
    </div>
  );
}

function ContactEditor({ form, setForm }) {
  const update = (field, value) => setForm({ ...form, contact: { ...form.contact, [field]: value } });

  return (
    <div>
      <FormGroup label="Titre de la page">
        <input type="text" value={form.contact.title} onChange={e => update('title', e.target.value)} className="admin-input" />
      </FormGroup>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <FormGroup label="Téléphone">
          <input type="text" value={form.contact.phone} onChange={e => update('phone', e.target.value)} className="admin-input" />
        </FormGroup>
        <FormGroup label="Email">
          <input type="email" value={form.contact.email} onChange={e => update('email', e.target.value)} className="admin-input" />
        </FormGroup>
      </div>
      <FormGroup label="Page Facebook">
        <input type="url" value={form.contact.facebook} onChange={e => update('facebook', e.target.value)} className="admin-input" />
      </FormGroup>
      <FormGroup label="Adresse">
        <textarea value={form.contact.address} onChange={e => update('address', e.target.value)} rows={2} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>
      <FormGroup label="Accès transports en commun">
        <textarea value={form.contact.transport} onChange={e => update('transport', e.target.value)} rows={4} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>
      <FormGroup label="Image">
        <input type="text" value={form.contact.image} onChange={e => update('image', e.target.value)} className="admin-input" placeholder="/uploads/..." />
        {form.contact.image && <img src={form.contact.image} alt="Preview" style={{ marginTop: '8px', maxHeight: '100px', borderRadius: '6px' }} />}
      </FormGroup>
    </div>
  );
}

function HistoryEditor({ form, setForm }) {
  const update = (field, value) => setForm({ ...form, history: { ...form.history, [field]: value } });

  return (
    <div>
      <FormGroup label="Titre de la page">
        <input type="text" value={form.history.title} onChange={e => update('title', e.target.value)} className="admin-input" />
      </FormGroup>

      <SectionTitle>Paragraphes</SectionTitle>
      <p style={{ fontSize: '12px', color: '#aaa', marginBottom: '12px' }}>
        Le contenu peut inclure du HTML (ex: &lt;strong&gt;texte en gras&lt;/strong&gt;, &lt;em&gt;italique&lt;/em&gt;).
      </p>
      {form.history.paragraphs.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'flex-start' }}>
          <span style={{ color: '#ccc', fontSize: '12px', marginTop: '12px', flexShrink: 0, width: '20px', textAlign: 'center' }}>{i + 1}</span>
          <textarea value={p} onChange={e => {
            const paragraphs = [...form.history.paragraphs];
            paragraphs[i] = e.target.value;
            update('paragraphs', paragraphs);
          }} rows={3} className="admin-input" style={{ resize: 'vertical', flex: 1 }} />
          <button onClick={() => update('paragraphs', form.history.paragraphs.filter((_, j) => j !== i))} className="admin-btn admin-btn-danger" style={{ marginTop: '6px', flexShrink: 0 }}>
            <FaTimes />
          </button>
        </div>
      ))}
      <AddButton onClick={() => update('paragraphs', [...form.history.paragraphs, ''])}>
        Ajouter un paragraphe
      </AddButton>

      <FormGroup label="Image principale">
        <input type="text" value={form.history.mainImage} onChange={e => update('mainImage', e.target.value)} className="admin-input" placeholder="/uploads/..." />
        {form.history.mainImage && <img src={form.history.mainImage} alt="Preview" style={{ marginTop: '8px', maxHeight: '100px', borderRadius: '6px' }} />}
      </FormGroup>

      <SectionTitle>Galerie d'images</SectionTitle>
      {form.history.galleryImages.map((img, i) => (
        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
          <input type="text" value={img.src} onChange={e => {
            const galleryImages = [...form.history.galleryImages];
            galleryImages[i] = { ...galleryImages[i], src: e.target.value };
            update('galleryImages', galleryImages);
          }} className="admin-input" style={{ flex: 2 }} placeholder="URL de l'image" />
          <input type="text" value={img.alt} onChange={e => {
            const galleryImages = [...form.history.galleryImages];
            galleryImages[i] = { ...galleryImages[i], alt: e.target.value };
            update('galleryImages', galleryImages);
          }} className="admin-input" style={{ flex: 1 }} placeholder="Texte alternatif" />
          <button onClick={() => update('galleryImages', form.history.galleryImages.filter((_, j) => j !== i))} className="admin-btn admin-btn-danger" style={{ flexShrink: 0 }}>
            <FaTimes />
          </button>
        </div>
      ))}
      <AddButton onClick={() => update('galleryImages', [...form.history.galleryImages, { src: '', alt: '' }])}>
        Ajouter une image
      </AddButton>
    </div>
  );
}

function RulesEditor({ form, setForm }) {
  const update = (field, value) => setForm({ ...form, rules: { ...form.rules, [field]: value } });

  return (
    <div>
      <FormGroup label="Titre de la page">
        <input type="text" value={form.rules.title} onChange={e => update('title', e.target.value)} className="admin-input" />
      </FormGroup>
      <FormGroup label="Introduction" hint="Peut contenir du HTML (ex: <strong>texte en gras</strong>).">
        <textarea value={form.rules.intro} onChange={e => update('intro', e.target.value)} rows={3} className="admin-input" style={{ resize: 'vertical' }} />
      </FormGroup>

      <SectionTitle>Règles</SectionTitle>
      {form.rules.items.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
          <span style={{ color: '#ccc', fontSize: '12px', flexShrink: 0, width: '20px', textAlign: 'center' }}>{i + 1}</span>
          <input type="text" value={item} onChange={e => {
            const items = [...form.rules.items];
            items[i] = e.target.value;
            update('items', items);
          }} className="admin-input" style={{ flex: 1 }} />
          <button onClick={() => update('items', form.rules.items.filter((_, j) => j !== i))} className="admin-btn admin-btn-danger" style={{ flexShrink: 0 }}>
            <FaTimes />
          </button>
        </div>
      ))}
      <AddButton onClick={() => update('items', [...form.rules.items, ''])}>
        Ajouter une règle
      </AddButton>

      <FormGroup label="Image">
        <input type="text" value={form.rules.image} onChange={e => update('image', e.target.value)} className="admin-input" placeholder="/uploads/..." />
        {form.rules.image && <img src={form.rules.image} alt="Preview" style={{ marginTop: '8px', maxHeight: '100px', borderRadius: '6px' }} />}
      </FormGroup>
    </div>
  );
}

// Shared UI components
function SectionTitle({ children }) {
  return (
    <h3 style={{
      marginTop: '32px', marginBottom: '16px', fontSize: '15px',
      fontWeight: '600', color: '#1a1a2e',
      paddingBottom: '8px', borderBottom: '1px solid #f0f0f0'
    }}>
      {children}
    </h3>
  );
}

function AddButton({ onClick, children }) {
  return (
    <button onClick={onClick} style={{
      background: '#f8f9fa', border: '2px dashed #d0d5dd', padding: '12px 16px',
      borderRadius: '8px', cursor: 'pointer', fontSize: '13px', width: '100%',
      marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '8px', color: '#888', fontWeight: '500', transition: 'all 0.2s'
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = '#32afe5'; e.currentTarget.style.color = '#32afe5'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = '#d0d5dd'; e.currentTarget.style.color = '#888'; }}
    >
      <FaPlus style={{ fontSize: '11px' }} /> {children}
    </button>
  );
}

function ItemCard({ children, onDelete }) {
  return (
    <div style={{
      padding: '20px', background: '#f8f9fa', borderRadius: '10px',
      marginBottom: '12px', position: 'relative', border: '1px solid #eee'
    }}>
      {children}
      {onDelete && (
        <button onClick={onDelete} style={{
          position: 'absolute', top: '12px', right: '12px',
          background: 'none', border: 'none', color: '#ccc',
          cursor: 'pointer', fontSize: '14px', padding: '4px',
          transition: 'color 0.2s'
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#e53232'}
        onMouseLeave={e => e.currentTarget.style.color = '#ccc'}
        >
          <FaTimes />
        </button>
      )}
    </div>
  );
}

export default SiteContentEditor;
